import React, { useRef, useEffect } from "react";
import * as THREE from "three";
import { Line } from "@react-three/drei";
import * as math from "mathjs";

function computeClassicIntensity(x, d, w, scale, distance, slit) {
  let leftIntensity = 0;
  let rightIntensity = 0;
  // Gauß-Profil für den linken Spalt
  if (slit === 1 || slit === 2) {
    const leftSlitCenter = math.divide(math.unaryMinus(d), 2);
    leftIntensity = math.exp(
      math.unaryMinus(
        math.pow(
          math.divide(
            math.subtract(math.divide(x, scale), leftSlitCenter),
            math.divide(w, 2)
          ),
          2
        )
      )
    );
  }
  if (slit === 0 || slit === 2) {
    // Gauß-Profil für den rechten Spalt
    const rightSlitCenter = math.divide(d, 2);
    rightIntensity = math.exp(
      math.unaryMinus(
        math.pow(
          math.divide(
            math.subtract(math.divide(x, scale), rightSlitCenter),
            math.divide(w, 2)
          ),
          2
        )
      )
    );
  }

  // Gesamte Intensität ist die Summe der beiden Profile
  const totalIntensity = math.divide(math.add(leftIntensity, rightIntensity), 2);

  return totalIntensity;
}

function computeIntensity(x, lambda, d, w, scale, distance) {
  // Der Schirm ist bei einer Skalierung von 1 immer 20mm (0.02m) breit

  // Skalierung nur in x-Richtung
  const beta = math.divide(
    math.divide(math.multiply(math.pi, w, x), scale),
    math.multiply(lambda, distance)
  );
  const gamma = math.divide(
    math.divide(math.multiply(math.pi, d, x), scale),
    math.multiply(lambda, distance)
  );

  if (math.isNaN(beta) || math.isNaN(gamma) || beta === 0) return 1; // Vermeidung von NaN-Werten
  const intensity = math.multiply(
    math.pow(math.divide(math.sin(beta), beta), 2),
    math.pow(math.cos(gamma), 2)
  );
  return intensity;
}

const computeSingleIntensity = (x, lambda, d, w, scale, distance, slit) => {
  // Der Schirm ist bei einer Skalierung von 1 immer 20mm (0.02m) breit

  let slitShift = slit === 1 ? d : math.multiply(d, -1);
  const adjustedX = math.subtract(x, slitShift);

  // Skalierung nur in x-Richtung, angepasste X-Position verwenden
  const beta = math.divide(
    math.divide(math.multiply(math.pi, w, adjustedX), scale),
    math.multiply(lambda, distance)
  );

  if (isNaN(beta) || beta === 0) return 1; // Vermeidung von NaN-Werten
  const intensity = Math.pow(Math.sin(beta) / beta, 2); // Einzelspalt-Diffraktion
  return intensity;
};

function IntensityBarChart({ intensityMap = {}, distance, binSize, scale }) {
  const bars = [];

  const maxCount = Math.max(
    ...Object.values(intensityMap).map((item) => item.count),
    0
  ); // Max count, at least 1 to avoid division by zero

  Object.keys(intensityMap).forEach((key) => {
    const bin = parseFloat(key);
    const count = intensityMap[key].count || 0;
    const normalizedHeight = (count / maxCount) * 4; // Normiere auf maximale Höhe von 4
    bars.push(
      new THREE.Vector3(bin, normalizedHeight / 2 - 2, -distance + 0.07)
    ); // Platziere am unteren Rand des Schirms
  });

  return (
    <group>
      {bars.map((bar, index) => (
        <mesh key={index} position={bar}>
          <boxGeometry args={[binSize * 2, bar.y * 2 + 4, binSize * 2]} />{" "}
          {/* Verdopple die Höhe für die Box, da sie vom Mittelpunkt aus wächst */}
          <meshStandardMaterial color="green" />
        </mesh>
      ))}
    </group>
  );
}

function IntensityCurve({
  lambda,
  d,
  w,
  distance,
  scale,
  isDoubleSlit,
  classic,
}) {
  const points = [];
  const resolution = 2000;
  const screenWidthInMeters = 2; // 200 cm = 2 Meter
  const screenUnits = 4; // 4 Einheiten im Koordinatensystem der Software
  const scaleFactor = screenWidthInMeters / screenUnits; // Skalierungsfaktor von Einheiten zu Metern
  for (let i = 0; i <= resolution; i++) {
    // Konvertierung des Wertes in das physikalische System (Meter)
    const x = (i / resolution - 0.5) * 4; // Wert in Software-Einheiten
    const xInMeters = x * scaleFactor; // Umrechnung in Meter (physikalisch)
    const xUnit = math.unit(xInMeters, "m"); // Einheit in Metern

    let intens = [];
    if (classic) {
      intens = computeClassicIntensity(
        math.unit(xInMeters, "m"),
        d,
        w,
        scale,
        distance,
        isDoubleSlit
      );
    } else {
      if (isDoubleSlit === 2) {
        intens = computeIntensity(xUnit, lambda, d, w, scale, distance);
      } else {
        intens = computeSingleIntensity(
          xUnit,
          lambda,
          d,
          w,
          scale,
          distance,
          isDoubleSlit
        );
      }
    }
    // Berechnung der Intensität mit dem umgerechneten Wert
    const intensity = intens;

    // Hinzufügen des Punktes zur Visualisierung
    points.push(
      new THREE.Vector3(
        x,
        intensity * 3.8 - 1.8,
        -distance.toNumber("m") + 0.08
      )
    ); // Visualisierung
  }

  //console.log(points);

  return <Line points={points} color="blue" lineWidth={4} />;
}

function InterferencePattern({ particles }) {
  const meshRef = useRef();

  const particleCount = particles.length;
  const positions = new Float32Array(particleCount * 3);

  particles.forEach((particle, index) => {
    positions.set([particle.x, particle.y, particle.z + 0.06], index * 3);
  });

  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3)
      );
      meshRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <points ref={meshRef}>
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={particleCount}
          itemSize={3}
          usage={THREE.DynamicDrawUsage}
        />
      </bufferGeometry>
      <pointsMaterial attach="material" color="red" size={0.016} />
    </points>
  );
}

export {
  IntensityBarChart,
  IntensityCurve,
  InterferencePattern,
  computeIntensity,
  computeSingleIntensity,
  computeClassicIntensity,
};
